import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { isPricingLive } from "@/config/pricing";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import UniversityImage from "@/components/UniversityImage";
import { Shield, Target, Mountain, Sparkles, ArrowRight, Lock } from "lucide-react";
import { startGoogleLogin } from "@/lib/login";
import usePageMeta from "@/lib/usePageMeta";

const BANDS = {
  safe: {
    label: "Safe",
    icon: <Shield size={18} strokeWidth={1.8} />,
    eyebrow: "Safety net",
    blurb: "Strong likelihood of admission. Anchor your shortlist here.",
    accentBg: "bg-emerald-50",
    accentBorder: "border-emerald-200",
    accentText: "text-[var(--success)]",
    dot: "bg-emerald-500",
  },
  target: {
    label: "Target",
    icon: <Target size={18} strokeWidth={1.8} />,
    eyebrow: "On the money",
    blurb: "Your profile matches the median admit. Plan a focused application.",
    accentBg: "bg-[var(--ind-soft)]",
    accentBorder: "border-[var(--ind)]",
    accentText: "text-[var(--ind)]",
    dot: "bg-[var(--ind)]",
  },
  reach: {
    label: "Reach",
    icon: <Mountain size={18} strokeWidth={1.8} />,
    eyebrow: "Ambitious stretch",
    blurb: "Below the median or highly selective. Worth a swing if the fit is right.",
    accentBg: "bg-amber-50",
    accentBorder: "border-amber-200",
    accentText: "text-[var(--warning)]",
    dot: "bg-amber-500",
  },
};

export default function TopPicks() {
  usePageMeta({ title: "Top university picks for your profile", description: "Ranked university picks for IB and Indian students, with the reasons behind each fit score.", path: "/top-picks" });
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLocked = isPricingLive() && !user?.is_paid;

  const startGoogle = () => {
    startGoogleLogin("/dashboard");
  };

  useEffect(() => {
    (async () => {
      try {
        // Single endpoint, single ranking engine — same for guests (DEMO_PROFILE) and signed-in users.
        const { data } = await api.get("/recommendations/top-picks");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">
                <Sparkles size={12} className="text-[var(--ind)]" strokeWidth={2.2} />
                Decision Engine
              </span>
            </div>
            <h1 className="mt-3 text-h1 text-[var(--s900)]" data-testid="top-picks-headline">
              Your top 10 universities, <em className="italic text-[var(--ind)]">sorted by realism.</em>
            </h1>
            <p className="text-body text-[var(--s600)] mt-4 max-w-2xl leading-relaxed">
              We&apos;ve grouped your matches into <span className="text-[var(--success)] font-medium">Safe</span>, <span className="text-[var(--ind)] font-medium">Target</span> and <span className="text-[var(--warning)] font-medium">Reach</span> — the same framework experienced counsellors use. Every label is computed from your IB profile, the median admit, and the university&apos;s acceptance rate.
            </p>
          </div>
          {data?.profile && (
            <div className="card p-4 min-w-[220px]" data-testid="top-picks-profile">
              <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)] mb-1">Based on</div>
              <div className="text-h3 font-semibold text-[var(--s900)]">
                IB {data.profile.predicted_ib_score} / 45
              </div>
              <div className="text-label text-[var(--s500)] mt-1">
                {(data.profile.target_countries || []).join(" · ") || "All countries"}
              </div>
              <button
                onClick={() => nav("/profile")}
                className="btn-link mt-3 inline-flex items-center gap-1 text-label"
                data-testid="top-picks-edit-profile"
              >
                Refine profile <ArrowRight size={12} strokeWidth={2.2} />
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center py-20 text-body-sm text-[var(--s400)]">Computing your top picks…</div>
        )}

        {data && !loading && (
          <>
            {/* Three-column band layout — blurred for non-logged-in users */}
            <div className="relative mt-10">
              <div className={`grid md:grid-cols-3 gap-6 ${!user ? "blur-[6px] select-none pointer-events-none" : ""}`}>
                {(["safe", "target", "reach"]).map(k => (
                  <BandColumn key={k} bandKey={k} unis={data[k] || []} onOpen={(slug) => nav(`/universities/${slug}`)} isLocked={isLocked} navigate={nav} />
                ))}
              </div>

              {!user && (
                <div className="absolute inset-0 flex items-start justify-center pt-32 px-4">
                  <div className="text-center max-w-md bg-white/85 backdrop-blur-md border border-[var(--s200)] rounded-[var(--r20)] shadow-xl p-8">
                    <div className="w-12 h-12 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
                      <Lock size={20} strokeWidth={1.8} />
                    </div>
                    <h3 className="text-h2 font-semibold text-[var(--s900)] leading-tight">
                      Sign in to reveal your AI Recommendations
                    </h3>
                    <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed">
                      Your 10 best-fit universities, sorted into Safe · Target · Reach based on your IB profile, budget and goals.
                    </p>
                    <button
                      onClick={startGoogle}
                      data-testid="tp-unlock"
                      className="btn-primary mt-5 inline-flex items-center justify-center gap-2"
                    >
                      Start free
                    </button>
                    <div className="mt-4 text-micro text-[var(--s500)] space-y-1">
                      <div><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</div>
                      <div><span className="text-[var(--success)] font-semibold">✓</span> Free to explore</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footnote */}
            <p className="text-label text-[var(--s400)] mt-12 max-w-2xl">
              Bucketing is based on your predicted IB vs each university&apos;s reported median, and on
              published acceptance rates. Estimates only — confirm requirements with the institution.
              Use Unimora AI Mentor for nuanced trade-off discussion.
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function BandColumn({ bandKey, unis, onOpen, isLocked, navigate }) {
  const c = BANDS[bandKey];
  return (
    <div className="flex flex-col" data-testid={`band-section-${bandKey}`}>
      {/* Column header */}
      <div className={`rounded-[var(--r12)] border ${c.accentBorder} ${c.accentBg} p-5 mb-4`}>
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-full flex items-center justify-center bg-white ${c.accentText} shadow-sm`}>
            {c.icon}
          </span>
          <div>
            <div className={`text-micro font-semibold tracking-[0.05em] uppercase ${c.accentText}`}>{c.eyebrow}</div>
            <h2 className="text-h2 font-semibold text-[var(--s900)] leading-tight">
              {c.label} picks <span className="text-[var(--s400)] text-body-lg font-normal">· {unis.length}</span>
            </h2>
          </div>
        </div>
        <p className="text-body-sm text-[var(--s600)] leading-relaxed mt-3">{c.blurb}</p>
      </div>

      {/* Cards stacked vertically in this column */}
      <div className="flex flex-col gap-4 flex-1">
        {unis.map((u, i) => {
          // Free users see only pick #1 per band (safe #1, target #1, reach #1 = 3 total)
          const locked = isLocked && i >= 1;
          return (
            <div key={u.slug} style={{ position: "relative" }}>
              <div
                style={{
                  filter: locked ? "blur(4px)" : "none",
                  pointerEvents: locked ? "none" : "auto",
                  userSelect: locked ? "none" : "auto",
                }}
              >
                <PickCard u={u} band={c} rank={i + 1} onOpen={() => onOpen(u.slug)} />
              </div>
              {locked && i === 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "white",
                    border: "1px solid var(--s200)",
                    borderRadius: "12px",
                    padding: "20px 24px",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(15,23,42,0.1)",
                    zIndex: 10,
                    width: "240px",
                  }}
                  data-testid={`tp-unlock-prompt-${bandKey}`}
                >
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "var(--s900)", marginBottom: "6px" }}>
                    See all picks
                  </p>
                  <p style={{ fontSize: "12px", color: "var(--s500)", marginBottom: "14px", lineHeight: 1.5 }}>
                    Including your best Target and Reach options
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    style={{
                      background: "var(--ind)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    Unlock full list →
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {unis.length === 0 && (
          <div className="card p-6 text-center text-body-sm text-[var(--s400)]">
            No matches — try widening your target countries.
          </div>
        )}
      </div>
    </div>
  );
}

function PickCard({ u, band, rank, onOpen }) {
  const total = (u.tuition_per_year_inr || 0) + (u.living_per_year_inr || 0);
  return (
    <div
      className="card overflow-hidden cursor-pointer transition-all hover:-translate-y-0.5 hover:border-[var(--s300)]"
      onClick={onOpen}
      data-testid={`pick-${u.slug}`}
    >
      <UniversityImage
        slug={u.slug}
        name={u.name}
        aspectClass="aspect-[21/9]"
        rounded=""
        showGradientOverlay
      />
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${band.dot}`} aria-hidden />
              <span className={`text-micro font-semibold tracking-[0.05em] uppercase ${band.accentText}`}>
                {band.label} · #{rank}
              </span>
            </div>
            <div className="text-h3 font-semibold text-[var(--s900)] leading-snug">{u.name}</div>
            <div className="text-label text-[var(--s500)] mt-1">{u.city} · {u.country}</div>
          </div>
          <div className="text-right">
            <div className="text-h2 font-semibold text-[var(--ind)] leading-none" data-testid={`fit-score-${u.slug}`}>{Math.round(u.fit_score)}</div>
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)] mt-1">Fit</div>
          </div>
        </div>

        {/* Quick metrics */}
        <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-[var(--s200)]">
          <Bit k="QS" v={`#${u.qs_rank_2025}`} />
          <Bit k="IB med." v={`${u.ib_score_median}/45`} />
          <Bit k="Accept." v={`${u.acceptance_rate_pct}%`} />
        </div>

        {/* Rationale */}
        <div className={`mt-5 rounded-[var(--r8)] p-3.5 border ${band.accentBorder} ${band.accentBg}`}>
          <div className={`text-micro font-semibold tracking-[0.05em] uppercase ${band.accentText} mb-1.5`}>
            Why {band.label.toLowerCase()} for you
          </div>
          <p className="text-body-sm leading-relaxed text-[var(--s700)]">{u.band_rationale}</p>
        </div>

        {/* Cost */}
        <div className="mt-4 flex items-center justify-between text-body-sm">
          <span className="text-[var(--s500)]">Total / year</span>
          <span className="font-semibold text-[var(--s900)]">{fmtINR(total)}</span>
        </div>
      </div>
    </div>
  );
}

function Bit({ k, v }) {
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className="text-body-sm font-medium text-[var(--s900)] mt-0.5">{v}</div>
    </div>
  );
}
